import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import styled from 'styled-components/macro';

import {
  listenNotifications,
  Notifications,
  stopListenNotifications,
} from '../../helpers/notifications';
import { getCurrentUser } from '../../helpers/settings';
import { formatNumber } from '../../helpers/utils';
import UsersService from '../../services/UsersService';
import { CurrencyStore, UserStore } from '../../store';
import { changeCurrency } from '../../utils/api';
import { Defaults } from '../common';
import SelectEx, { SelectExOptionProps } from '../controls/SelectEx';
import { currency } from '../currencies';

interface UserBalanceProps {
  height: number | string;
  authorized?: boolean;
}

const UserBalanceSelect = ({ height, authorized = false }: UserBalanceProps) => {
  const { fiatCurrency, balances, getBalances, setFiatCurrency } = CurrencyStore;
  const { updateUser } = UserStore;

  useEffect(() => {
    getBalances();
    getCurrentFiatCurrency();
    listenNotifications(handleNotifications);

    return () => stopListenNotifications(handleNotifications);
  }, []);

  const getCurrentFiatCurrency = () => {
    UsersService.profile({ id: getCurrentUser() }, (profile) => {
      const currency = profile?.currency?.toUpperCase() || 'RUB';
      setFiatCurrency(currency);
    });
  };

  const handleNotifications = (notifications: Notifications) => {
    notifications.unreadGroups > 0 &&
      Object.keys(notifications.deals).length > 0 &&
      getBalances();
  };

  const onChangeCurrency = (currency: string) => {
    changeCurrency(currency)
      .then(() => setFiatCurrency(currency, true))
      .finally(updateUser);
  };

  const options: SelectExOptionProps[] = toJS(balances).map((balance) => {
    return {
      id: balance.currency,
      value: formatNumber(balance.amount, 1) + ' ' + balance.currency,
      imgPosition: 'right',
      img: (
        <OptionImgContainer>
          {currency(balance.currency, undefined, Defaults.neroColor)}
        </OptionImgContainer>
      ),
      font: { weight: '400' },
    };
  });

  return (
    <UserBalanceSelectContainer>
      <SelectEx
        authorized={authorized}
        height={height}
        borderRadius={'8px'}
        id={fiatCurrency}
        arrowColor={Defaults.silverGray}
        backgroundColor={Defaults.inputsBgColor}
        arrowType={'compact'}
        options={options}
        caption={'top-bar.user-currency'}
        captionSize={'12px'}
        captionColor={Defaults.silverGray}
        onChange={(c) => onChangeCurrency(c)}
        optionPaddingLeft={'12px'}
        optionPaddingRight={'2px'}
      />
    </UserBalanceSelectContainer>
  );
};

const UserBalanceSelectContainer = styled.div`
  height: 48px;

  @media (max-width: 1024px) {
    height: 40px;
  }
`;

const OptionImgContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 40px;
  min-width: 40px;
  min-height: 40px;
  border-radius: 4px;
  margin-left: auto;
  background-color: ${Defaults.lightBlue};

  @media (max-width: 1024px) {
    height: 32px;
    width: 32px;
    min-width: 32px;
    min-height: 32px;
  }
`;

export default observer(UserBalanceSelect);
